// src/pages/yachts/special-offers/EditSpecialOfferContainer.js
import React from 'react';
import moment from 'moment';
import { connect } from 'react-redux';
import { SubmissionError } from 'redux-form';

import ManageSpecialOfferPresenter from './ManageSpecialOfferPresenter';
import buildOfferPayload from './buildOfferPayload';
import parseApiError from './parseApiError';
import {
    fetchSpecialOffer,
    updateSpecialOffer,
    fetchOfferYachtRates,
    fetchOfferYachtSeasons
} from './redux/_actions';

function toDateInput(value) {
    if (!value) return '';
    // moment parses both ISO datetimes ('2026-07-05T00:00:00.000Z') and bare dates.
    const m = moment(value);
    return m.isValid() ? m.format('YYYY-MM-DD') : '';
}

function toDatetimeLocalInput(value) {
    if (!value) return '';
    const m = moment(value);
    // HTML datetime-local needs 'YYYY-MM-DDTHH:mm' in the LOCAL timezone.
    return m.isValid() ? m.format('YYYY-MM-DDTHH:mm') : '';
}

function shapeInitialValues(offer) {
    if (!offer) return undefined;
    return {
        ...offer,
        is_active: !!offer.is_active,
        allow_partial_application: !!offer.allow_partial_application,
        date_from: toDateInput(offer.date_from),
        date_to: toDateInput(offer.date_to),
        booking_window_start: toDateInput(offer.booking_window_start),
        booking_window_end: toDateInput(offer.booking_window_end),
        publish_start_date: toDatetimeLocalInput(offer.publish_start_date),
        publish_end_date: toDatetimeLocalInput(offer.publish_end_date),
        tiers: (offer.tiers || []).map((t) => ({ ...t })),
        allowed_broker_ids: offer.allowed_broker_ids || [],
        location_ids: offer.location_ids || []
    };
}

class EditSpecialOfferContainer extends React.Component {
    state = {
        loading: true,
        submitError: null,
        conflictOfferId: null,
        simulateOpen: false,
        warnings: []
    };

    componentDidMount() {
        const { id, offerId } = this.props.match.params;
        Promise.all([
            this.props.fetchOfferYachtRates(id),
            this.props.fetchOfferYachtSeasons(id),
            this.props.fetchSpecialOffer(offerId)
        ]).finally(() => this.setState({ loading: false }));
    }

    openSimulate = () => this.setState({ simulateOpen: true });
    closeSimulate = () => this.setState({ simulateOpen: false });

    onSubmit = (values) => {
        const yachtId = Number(this.props.match.params.id);
        const offerId = Number(this.props.match.params.offerId);
        return this.props
            .updateSpecialOffer(offerId, buildOfferPayload(values, yachtId))
            .then((data) => {
                // The offer saved either way; warnings are held on screen so the admin
                // sees them in context instead of losing them to the redirect.
                if (data && data.warnings && data.warnings.length) {
                    this.setState({ submitError: null, warnings: data.warnings });
                    return;
                }
                this.props.history.push(
                    `/yachts/view/charter/${yachtId}/special-offers`
                );
            })
            .catch((error) => {
                const { message, conflictOfferId, fieldErrors } = parseApiError(error);
                this.setState({ submitError: message, conflictOfferId, warnings: [] });
                // Also surfaces the message inline under the field it names.
                if (fieldErrors) throw new SubmissionError(fieldErrors);
            });
    };

    render() {
        return (
            <ManageSpecialOfferPresenter
                loading={this.state.loading}
                isEdit={true}
                user_role={this.props.user_role}
                match={this.props.match}
                history={this.props.history}
                onSubmit={this.onSubmit}
                submitError={this.state.submitError}
                conflictOfferId={this.state.conflictOfferId}
                warnings={this.state.warnings}
                warningsHref={`/yachts/view/charter/${this.props.match.params.id}/special-offers`}
                initialValues={shapeInitialValues(this.props.current)}
                simulateOpen={this.state.simulateOpen}
                onOpenSimulate={this.openSimulate}
                onCloseSimulate={this.closeSimulate}
            />
        );
    }
}

const mapStateToProps = (state) => ({
    user_role: state.auth.user_role,
    current: state.yacht_special_offers.current
});

const mapDispatchToProps = (dispatch) => ({
    fetchSpecialOffer: (id) => dispatch(fetchSpecialOffer(id)),
    updateSpecialOffer: (id, payload) => dispatch(updateSpecialOffer(id, payload)),
    fetchOfferYachtRates: (id) => dispatch(fetchOfferYachtRates(id)),
    fetchOfferYachtSeasons: (id) => dispatch(fetchOfferYachtSeasons(id))
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(EditSpecialOfferContainer);
